<template>
  <div class="project-management">
    <div class="pm-toolbar">
      <div class="pm-search flex-grow-1">
        <b-input-group>
          <b-input-group-prepend is-text>
            <feather-icon
              icon="SearchIcon"
              size="16"
              class="text-muted"
            />
          </b-input-group-prepend>
          <b-form-input
            v-model.trim="searchTerm"
            :placeholder="$t('Find a board...')"
          />
        </b-input-group>
      </div>

      <div class="pm-filters">
        <b-dropdown
          right
          variant="outline-secondary"
          toggle-class="filter-pill"
          :text="typeLabel"
        >
          <b-dropdown-item
            v-for="option in typeOptions"
            :key="option.value"
            @click="typeFilter = option.value"
          >
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>
        <b-dropdown
          right
          variant="outline-secondary"
          toggle-class="filter-pill"
          :text="sortLabel"
          class="ml-50"
        >
          <b-dropdown-item
            v-for="option in sortOptions"
            :key="option.value"
            @click="sortBy = option.value"
          >
            {{ option.text }}
          </b-dropdown-item>
        </b-dropdown>
      </div>

      <div class="pm-actions">
        <b-button
          v-if="canEditBoards"
          variant="primary"
          @click="showCreateBoardModal = true"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          {{ $t('New Board') }}
        </b-button>
      </div>

      <manage-permissions
        v-if="canManagePermissions"
        :permissions="permissions"
      />
    </div>

    <b-skeleton-wrapper :loading="loading">
      <template #loading>
        <b-row>
          <b-col
            v-for="i in IS_MOBILE() ? 2 : 8"
            :key="i"
            md="3"
            class="mb-2"
          >
            <b-card>
              <b-skeleton class="w-25" />
              <b-skeleton class="w-100" />

              <div class="avatar-group">
                <b-skeleton type="avatar" />
                <b-skeleton type="avatar" />
                <b-skeleton type="avatar" />
              </div>
            </b-card>
          </b-col>
        </b-row>
      </template>

      <div v-if="!loading">
        <draggable
          v-if="localBoards.length"
          :list="localBoards"
          handle=".drag-handle"
          :disabled="!canEditBoards || sortBy !== 'order'"
          :animation="150"
          tag="div"
          class="row board-grid"
          @start="onDragStart"
          @end="onReorder"
        >
          <b-col
            v-for="board in localBoards"
            :key="board.uuid"
            cols="12"
            md="6"
            lg="4"
            xl="3"
            class="mb-1"
          >
            <board-card
              :board="board"
              :show-drag-handle="canEditBoards && sortBy === 'order'"
              :is-dragging="isDragging"
              @click="goToBoard(board)"
              @edit="editBoard(board)"
              @delete="confirmDeleteBoard(board)"
            />
          </b-col>
        </draggable>
        <b-card
          v-if="!localBoards.length"
          class="text-center py-5 empty-state"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h4 class="text-muted">
            {{ emptyStateTitle }}
          </h4>
          <p class="text-muted">
            {{ emptyStateSubtitle }}
          </p>
          <b-button
            v-if="canEditBoards"
            variant="primary"
            @click="showCreateBoardModal = true"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            {{ $t('Create Board') }}
          </b-button>
        </b-card>
      </div>
    </b-skeleton-wrapper>

    <!-- Create/Edit Board Modal -->
    <b-modal
      v-model="showCreateBoardModal"
      :title="editingBoard ? $t('Edit Board') : $t('Create New Board')"
      hide-footer
      centered
    >
      <b-form @submit.prevent="submitBoard">
        <b-form-group
          :label="$t('Board Name')"
          label-for="board-name"
        >
          <b-form-input
            id="board-name"
            v-model="boardForm.name"
            :placeholder="$t('Enter board name')"
            required
          />
        </b-form-group>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <b-button
              v-if="canManageBoards && editingBoard"
              variant="outline-secondary"
              size="sm"
              @click="openMembersFromIndex"
            >
              <feather-icon
                icon="UsersIcon"
                class="mr-1"
              />
              {{ $t('Manage Members') }}
            </b-button>
          </div>
          <div>
            <b-button
              variant="secondary"
              class="mr-1"
              @click="cancelBoardModal"
            >
              {{ $t('Cancel') }}
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="submitting"
            >
              <b-spinner
                v-if="submitting"
                small
                class="mr-50"
              />
              {{ editingBoard ? $t('Update') : $t('Create') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </b-modal>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      :title="$t('Delete Board')"
      hide-footer
      centered
    >
      <p>{{ $t('Are you sure you want to delete this board?') }}</p>
      <p class="text-danger">
        <strong>{{ boardToDelete?.name }}</strong>
      </p>
      <p class="text-muted small">
        {{ $t('This action cannot be undone. Tasks will not be deleted but will no longer be associated with this board.') }}
      </p>
      <div class="d-flex justify-content-end">
        <b-button
          variant="secondary"
          class="mr-1"
          @click="showDeleteModal = false"
        >
          {{ $t('Cancel') }}
        </b-button>
        <b-button
          variant="danger"
          :disabled="submitting"
          @click="deleteBoard"
        >
          <b-spinner
            v-if="submitting"
            small
            class="mr-50"
          />
          {{ $t('Delete') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Members Manager Modal -->
    <b-modal
      v-model="showMembersModal"
      :title="$t('Manage Board Members')"
      size="lg"
      hide-footer
      centered
    >
      <board-members-manager
        :board="editingBoard"
        @close="showMembersModal = false"
        @updated="onMembersUpdated"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BCard,
  BModal,
  BDropdown,
  BDropdownItem,
  BInputGroup,
  BInputGroupPrepend,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BSkeletonWrapper,
  BSkeleton,
} from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import EventBus from '@/event-bus'
import { checkPermission } from '@/utils/permissions'
import draggable from 'vuedraggable'
import BoardCard from './boards/BoardCard.vue'
import BoardMembersManager from './boards/BoardMembersManager.vue'
import ManagePermissions from '../common/components/ManagePermissions.vue'

export default {
  name: 'ProjectManagement',
  components: {
    BRow,
    BCol,
    BButton,
    BCard,
    BModal,
    BDropdown,
    BDropdownItem,
    BInputGroup,
    BInputGroupPrepend,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BSkeletonWrapper,
    BSkeleton,
    draggable,
    BoardCard,
    BoardMembersManager,
    ManagePermissions,
  },
  data() {
    return {
      showCreateBoardModal: false,
      showDeleteModal: false,
      showMembersModal: false,
      editingBoard: null,
      boardToDelete: null,
      submitting: false,
      boardForm: {
        name: '',
      },
      searchTerm: '',
      typeFilter: 'all',
      sortBy: 'order',
      localBoards: [],
      isReordering: false,
      isDragging: false,
      typeOptions: [
        { value: 'all', text: this.$t('All board types') },
        { value: 'small', text: this.$t('Up to 3 columns') },
        { value: 'medium', text: this.$t('4-5 columns') },
        { value: 'large', text: this.$t('6+ columns') },
      ],
      sortOptions: [
        { value: 'order', text: this.$t('Custom Order') },
        { value: 'updated_desc', text: this.$t('Newest updated') },
        { value: 'updated_asc', text: this.$t('Oldest updated') },
        { value: 'name_asc', text: this.$t('Name A - Z') },
        { value: 'name_desc', text: this.$t('Name Z - A') },
      ],
      permissions: ['boards.manage', 'boards.edit'],
      needPermissionCheck: false,
    }
  },
  computed: {
    ...mapGetters('boards', ['GET_BOARDS', 'GET_CURRENT_BOARD', 'GET_LOADING']),
    ...mapGetters('project', ['GET_CURRENT_PROJECT', 'USER_ROLE']),
    ...mapGetters('permissions', ['canManagePermissions', 'canManageBoards', 'canEditBoards']),
    boards() {
      return this.GET_BOARDS
    },
    loading() {
      return this.GET_LOADING
    },
    isManager() {
      return ['Project Owner', 'Project Manager', 'admin'].includes(this.USER_ROLE)
    },
    projectUid() {
      return this.GET_CURRENT_PROJECT?.uuid
    },
    filteredBoards() {
      const needle = this.searchTerm.trim().toLowerCase()
      const filtered = this.boards
        .filter(board => (!needle ? true : (board.name || '').toLowerCase().includes(needle)))
        .filter(board => {
          const columns = board.statuses?.length || 0
          if (this.typeFilter === 'small') return columns > 0 && columns <= 3
          if (this.typeFilter === 'medium') return columns >= 4 && columns <= 5
          if (this.typeFilter === 'large') return columns >= 6
          return true
        })

      return this.sortBoards(filtered)
    },
    isFiltering() {
      return !!this.searchTerm || this.typeFilter !== 'all'
    },
    typeLabel() {
      const match = this.typeOptions.find(option => option.value === this.typeFilter)
      return match ? match.text : this.$t('Type')
    },
    sortLabel() {
      const match = this.sortOptions.find(option => option.value === this.sortBy)
      return match ? match.text : this.$t('Sort')
    },
    emptyStateTitle() {
      if (this.loading) return this.$t('Loading...')
      return this.isFiltering ? this.$t('No boards match your filters') : this.$t('No boards found')
    },
    emptyStateSubtitle() {
      if (this.isFiltering) return this.$t('Adjust search or filters to see boards')
      return this.$t('Create your first board to get started')
    },
  },
  watch: {
    projectUid: {
      immediate: true,
      handler(val) {
        if (val) {
          this.loadBoards()
        }
      },
    },
    filteredBoards: {
      immediate: true,
      handler(val) {
        if (!this.isReordering) {
          this.localBoards = [...val]
        }
      },
    },
  },
  mounted() {
    EventBus.$on('permission-updated', permission => {
      if (this.permissions.indexOf(permission) !== -1) {
        this.needPermissionCheck = true
        setTimeout(() => this.checkUserPermission(), 2000)
      }
    })
    this.checkUserPermission()
  },
  beforeDestroy() {
    EventBus.$off('permission-updated')
  },
  methods: {
    ...mapActions('boards', ['clearCurrentBoard', 'fetchBoards', 'createBoard', 'updateBoard', 'updateBoardOrder', 'deleteBoard', 'fetchBoard']),
    ...mapActions('permissions', ['setPermissions']),
    checkUserPermission() {
      if (this.needPermissionCheck || !this.canManageBoards || !this.canEditBoards) {
        checkPermission(this.permissions, permission => {
          const canManageBoards = permission['boards.manage'] === true
          const canEditBoards = permission['boards.edit'] === true

          const payload = [
            { key: 'setCanManageBoards', value: canManageBoards },
            { key: 'setCanEditBoards', value: canEditBoards },
          ]

          this.setPermissions(payload)
          this.needPermissionCheck = false
        })
      }
    },
    sortBoards(boards) {
      const list = [...boards]
      const getDate = board => new Date(board.updatedAt || board.createdAt || 0).getTime()

      switch (this.sortBy) {
      case 'order':
        return list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      case 'name_asc':
        return list.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      case 'name_desc':
        return list.sort((a, b) => (b.name || '').localeCompare(a.name || ''))
      case 'updated_asc':
        return list.sort((a, b) => getDate(a) - getDate(b))
      case 'updated_desc':
      default:
        return list.sort((a, b) => getDate(b) - getDate(a))
      }
    },
    onDragStart() {
      this.$root.$emit('bv::hide::tooltip')
      this.isDragging = true
    },
    async onReorder() {
      this.isReordering = true
      try {
        const promises = this.localBoards.map((board, i) => {
          if ((board.order ?? 0) !== i) {
            return this.updateBoardOrder({
              projectUid: this.projectUid,
              boardUid: board.uuid,
              order: i,
            })
          }
          return Promise.resolve()
        })
        await Promise.all(promises)
        this.showSuccess(this.$t('Board order updated'))
      } catch (error) {
        this.showError(this.$t('Failed to save board order'))
      } finally {
        this.isReordering = false
        this.localBoards = [...this.filteredBoards]
        setTimeout(() => {
          this.$root.$emit('bv::hide::tooltip')
          this.isDragging = false
        }, 500)
      }
    },

    async loadBoards() {
      if (this.projectUid) {
        try {
          await this.fetchBoards(this.projectUid)
        } catch (error) {
          this.showError(error.message)
        }
      }
    },
    goToBoard(board) {
      if (this.GET_CURRENT_BOARD?.uuid !== board.uuid) {
        this.clearCurrentBoard()
      }
      this.$router.push({
        name: 'project-board',
        params: { boardUid: this.base58Converter(board.uuid) },
      })
    },
    editBoard(board) {
      this.editingBoard = board
      this.boardForm.name = board.name
      this.showCreateBoardModal = true
    },
    cancelBoardModal() {
      this.showCreateBoardModal = false
      this.showMembersModal = false
      setTimeout(() => {
        this.boardForm.name = ''
        this.editingBoard = null
      }, 500)
    },
    async submitBoard() {
      this.submitting = true
      try {
        if (this.editingBoard) {
          const result = await this.updateBoard({
            projectUid: this.projectUid,
            boardUid: this.editingBoard.uuid,
            input: { name: this.boardForm.name },
          })
          this.showSuccess(result.message)
        } else {
          const result = await this.createBoard({
            projectUid: this.projectUid,
            input: { name: this.boardForm.name },
          })
          this.showSuccess(result.message)
        }
        this.cancelBoardModal()
        this.loadBoards()
      } catch (error) {
        this.showError(this.$t('Error'))
      } finally {
        this.submitting = false
      }
    },
    // Called when members manager emits updated
    async onMembersUpdated() {
      await this.loadBoards()
      this.editingBoard = this.GET_BOARDS.find(board => board.uuid === this.editingBoard.uuid)
    },
    confirmDeleteBoard(board) {
      this.boardToDelete = board
      this.showDeleteModal = true
    },
    async openMembersFromIndex() {
      if (!this.editingBoard) return
      try {
        // Refresh latest board info
        await this.fetchBoard({ uuid: this.editingBoard.uuid })
        this.showMembersModal = true
      } catch (err) {
        this.showError(err.message)
      }
    },
    async deleteBoard() {
      this.submitting = true
      try {
        const result = await this.$store.dispatch('boards/deleteBoard', {
          projectUid: this.projectUid,
          boardUid: this.boardToDelete.uuid,
        })

        this.showSuccess(result.message)

        this.showDeleteModal = false
        this.boardToDelete = null
      } catch (error) {
        this.showError(this.$t('Error: Could not delete board!'))
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.project-management {
  padding: 1.5rem;
  min-height: 100%;
}

.pm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.pm-search :deep(.input-group-text) {
  background: transparent;
  border-right: 0;
}

.pm-search :deep(input) {
  border-left: 0;
  border-radius: 10px;
  height: 42px;
}

.pm-filters {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-pill {
  border-radius: 10px !important;
  border: 1px solid #d8dee9 !important;
  // color: #475569 !important;
  padding: 0.35rem 0.9rem !important;
  height: 42px;
  display: inline-flex;
  align-items: center;
}

.pm-actions {
  margin-left: auto;
}

.board-grid {
  margin-top: 0.25rem;
}

@media (max-width: 767px) {
  .pm-actions {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
}
</style>
